import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchSecurities,
  deleteOverride,
  saveOverride as saveOverrideService,
} from 'services/securities'
import { useNotification } from 'components/Notification'

function useSecuritySearch() {
  const queryClient = useQueryClient()
  const [securityOptions, setSecurityOptions] = useState([])
  const [pagination, setPagination] = useState({ pageSize: 50, page: 0 })
  const { createNotification } = useNotification()

  const securityIds = securityOptions ? securityOptions.map(security => security.value) : []

  const { data, isFetching } = useQuery({
    queryKey: ['fetchSecurities', { securityIds, ...pagination }],
    queryFn: () =>
      fetchSecurities(securityIds, {
        includeSectorOverride: true,
        pageSize: pagination.pageSize,
        pageOffset: pagination.page * pagination.pageSize,
      }),
    initialData: { data: [], meta: {} },
  })

  const { mutate: upsertOverride, isPending: isSaving } = useMutation({
    mutationFn: ({ sector, subsector, security }) => {
      if (!sector || !subsector) {
        throw new Error('A sector AND subsector must be specified')
      }

      return saveOverrideService({
        sectorCode: sector,
        subsectorCode: subsector,
        entityId: security.issuer.legacyId,
        hasOverride: !!security.issuer.sectorOverride,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchSecurities'] })
      createNotification({
        message: 'Successfully overrided security',
        variabt: 'success',
      })
    },
    onError: error => {
      createNotification({
        message: `Error trying to override - ${error?.message}`,
        variant: 'error',
      })
    },
  })

  const { mutate: removeOverride, isPending: isDeleting } = useMutation({
    mutationFn: ({ issuerLegacyId }) => deleteOverride({ issuerLegacyId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fetchSecurities'] }),
  })

  const handlePageChange = page => {
    setPagination(prev => ({ ...prev, page }))
  }

  const handlePageSizeChange = pageSize => {
    setPagination(prev => ({ ...prev, pageSize }))
  }

  return {
    securities: data.data,
    isFetching,
    isSaving,
    isDeleting,
    securityOptions,
    setSecurityOptions,
    removeOverride,
    saveOverride: upsertOverride,
    pagination,
    count: data.meta.count,
    handlePageChange,
    handlePageSizeChange,
  }
}

export default useSecuritySearch
